import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function BantryBayPage() {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    try {
      document.title =
        'Bantry Bay Painters Cape Town | Luxury Coastal Painting Services | Premium Properties';

      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute(
          'content',
          'Elite painting services in Bantry Bay, Cape Town. Specialist luxury residential painting for exclusive coastal properties. Expert marine-grade coatings and premium finishes for discerning clients.'
        );
      }

      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute(
          'content',
          'Bantry Bay painters, luxury painting Cape Town, coastal property painting'
        );
      }

      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute(
          'href',
          `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/bantry-bay`
        );
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'Bantry Bay Painters Cape Town',
        description: 'Elite painting services in Bantry Bay, Cape Town',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/bantry-bay`,
        areaServed: {
          '@type': 'Place',
          name: 'Bantry Bay, Cape Town, Western Cape, South Africa',
        },
        serviceArea: {
          '@type': 'GeoCircle',
          geoMidpoint: {
            '@type': 'GeoCoordinates',
            latitude: -33.9289,
            longitude: 18.3789,
          },
          geoRadius: '10000',
        },
      });
      document.head.appendChild(script);
    } catch (err) {
      console.error('Error setting up meta tags or JSON‑LD script:', err);
    }

    return () => {
      const existingScript = document.querySelector(
        'script[type="application/ld+json"]'
      );
      if (existingScript && existingScript.textContent?.includes('Bantry Bay Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-building-4-line',
      title: 'Luxury Villa Painting',
      description:
        'Premium painting services for Bantry Bay\'s exclusive villas and mansions with breathtaking ocean views. Our specialized approach ensures your luxury residence maintains its pristine appearance despite the challenging Atlantic coastal environment. We use only the finest marine-grade materials specifically formulated to withstand salt air, intense UV exposure, and moisture-rich conditions.',
      features: [
        'Marine-grade protection',
        'Premium luxury finishes',
        'Expert color consultation',
        'Minimal lifestyle disruption',
        'High-end materials',
        'Meticulous preparation'
      ],
    },
    {
      icon: 'ri-home-heart-line',
      title: 'Penthouse & Apartment Painting',
      description:
        'Specialized services for luxury penthouses and high-rise apartments in Bantry Bay\'s prestigious residential complexes. We understand the unique requirements of exclusive apartment living, offering flexible scheduling and efficient execution to minimize disruption while delivering exceptional results that enhance property value and aesthetic appeal.',
      features: [
        'High-rise expertise',
        'Luxury apartment standards',
        'Resident coordination',
        'Premium scheduling',
        'Body corporate liaison',
        'Quality assurance'
      ],
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Coastal Protection Systems',
      description:
        'Advanced protective coatings engineered to withstand Bantry Bay\'s harsh Atlantic coastal conditions including salt spray, wind-driven rain, and intense UV radiation. Our multi-layer protection systems provide superior defense against environmental factors, ensuring your property maintains its beauty and structural integrity for years with minimal maintenance.',
      features: [
        'Salt air resistance',
        'UV protection systems',
        'Weather sealing',
        'Long-term durability',
        'Anti-corrosion coatings',
        'Moisture barriers'
      ],
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Exterior Coastal Painting',
      description:
        'Comprehensive exterior painting services specifically designed for properties facing the Atlantic Ocean. Our marine-grade systems protect against salt spray, wind-driven rain, and intense UV radiation while maintaining stunning aesthetics that complement Bantry Bay\'s sophisticated architectural landscape and natural beauty.',
      features: [
        'Marine-grade systems',
        'Salt spray protection',
        'Wind resistance',
        'UV protection',
        'Color retention',
        'Extended warranty'
      ],
    },
    {
      icon: 'ri-home-smile-2-line',
      title: 'Interior Luxury Painting',
      description:
        'Exquisite interior painting services for Bantry Bay\'s most prestigious homes, featuring premium finishes, custom color schemes, and meticulous attention to detail. We work with the finest materials and employ master craftsmen who understand the exacting standards expected in this exclusive neighborhood.',
      features: [
        'Premium interior finishes',
        'Custom color matching',
        'Designer collaboration',
        'Low-VOC materials',
        'Furniture protection',
        'Detailed craftsmanship'
      ],
    },
    {
      icon: 'ri-tools-line',
      title: 'Waterproofing & Restoration',
      description:
        'Comprehensive waterproofing solutions and restoration services for coastal properties experiencing moisture issues or weather damage. Our expert approach addresses both aesthetic and structural concerns, ensuring your Bantry Bay property remains protected against the elements while maintaining its prestigious appearance.',
      features: [
        'Moisture remediation',
        'Structural protection',
        'Crack repair',
        'Seal restoration',
        'Preventive maintenance',
        'Comprehensive warranty'
      ],
    },
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Elite Property Assessment',
      description:
        'Comprehensive evaluation of your Bantry Bay property, including detailed inspection of coastal exposure, existing coating condition, and specific environmental challenges. We assess salt air impact, UV damage, and moisture issues to develop a customized protection strategy tailored to your property\'s unique location and requirements.',
    },
    {
      step: 2,
      title: 'Premium Material Selection',
      description:
        'Expert consultation on the finest marine-grade paints and coatings specifically formulated for Atlantic coastal properties. We recommend premium products from leading manufacturers that offer superior protection against harsh coastal conditions while achieving your desired aesthetic vision and complementing your property\'s architectural style.',
    },
    {
      step: 3,
      title: 'Meticulous Surface Preparation',
      description:
        'Thorough preparation including power washing, salt deposit removal, surface repairs, and premium primer application. Our detailed prep work ensures optimal adhesion and longevity of the coating system, addressing any underlying issues before painting begins to guarantee exceptional results.',
    },
    {
      step: 4,
      title: 'Expert Application',
      description:
        'Professional application by experienced master craftsmen using advanced techniques and premium equipment. We employ multiple coat systems with proper curing times between applications to ensure maximum durability, superior protection, and a flawless finish that meets the highest luxury standards.',
    },
    {
      step: 5,
      title: 'Quality Inspection',
      description:
        'Rigorous quality control inspection of all painted surfaces, checking for uniform coverage, proper adhesion, and flawless finish. We ensure every detail meets our exacting standards and your expectations before project completion, with comprehensive documentation of all work performed.',
    },
    {
      step: 6,
      title: 'Maintenance Guidance',
      description:
        'Comprehensive maintenance recommendations and care instructions to maximize the lifespan of your new coating system. We provide detailed guidance on cleaning, inspection schedules, and preventive maintenance specific to Bantry Bay\'s coastal environment and your property\'s unique requirements.',
    },
  ];

  const recentProjects = [
    {
      title: 'Oceanfront Villa Transformation',
      location: 'Bantry Bay Beachfront',
      description:
        'Complete luxury renovation of a 5-bedroom oceanfront villa with panoramic Atlantic views. This prestigious project included interior and exterior painting with premium marine-grade coatings, custom color consultation, specialized finishes throughout, and comprehensive waterproofing systems.',
      image:
        'https://public.readdy.ai/ai/img_res/ef116dfb12978210d1993a5569096d6d.jpg',
    },
    {
      title: 'Luxury Penthouse Complete Renovation',
      location: 'Bantry Bay Heights',
      description:
        'Comprehensive painting renovation for a 4-bedroom penthouse with 360-degree ocean and mountain views. Project included premium interior finishes, balcony waterproofing, and specialized coatings for high-altitude coastal exposure. Completed with minimal resident disruption.',
      image:
        'https://readdy.ai/api/search-image?query=Luxury%20penthouse%20painting%20in%20Bantry%20Bay%2C%20high-end%20apartment%20with%20ocean%20views%2C%20premium%20interior%20painting%2C%20modern%20coastal%20luxury%20residential%20design%20Cape%20Town&width=400&height=300&seq=bantry-bay-project-2&orientation=landscape',
    },
    {
      title: 'Historic Mansion Restoration',
      location: 'Bantry Bay Heritage Property',
      description:
        'Meticulous restoration of a 1920s heritage mansion including exterior facade restoration, interior period-appropriate finishes, and modern marine-grade protection systems. Project required specialized techniques to preserve architectural integrity while providing contemporary protection.',
      image:
        'https://readdy.ai/api/search-image?query=Historic%20mansion%20restoration%20painting%20in%20Bantry%20Bay%20Cape%20Town%2C%20heritage%20property%20with%20period%20architecture%2C%20professional%20restoration%20painting%2C%20elegant%20coastal%20estate%20with%20marine%20protection&width=400&height=300&seq=bantry-bay-project-3&orientation=landscape',
    },
    {
      title: 'Modern Architectural Masterpiece',
      location: 'Bantry Bay Contemporary Estate',
      description:
        'Complete painting of a newly constructed contemporary villa featuring cutting-edge architecture and premium finishes. Project included specialized coatings for glass, steel, and concrete surfaces, with emphasis on UV protection and salt air resistance.',
      image:
        'https://readdy.ai/api/search-image?query=Modern%20contemporary%20villa%20painting%20in%20Bantry%20Bay%2C%20architectural%20masterpiece%20with%20ocean%20views%2C%20premium%20painting%20services%2C%20sleek%20coastal%20design%20Cape%20Town%20with%20marine-grade%20finishes&width=400&height=300&seq=bantry-bay-project-4&orientation=landscape',
    },
  ];

  const testimonials = [
    {
      name: 'Victoria Ashford',
      location: 'Bantry Bay Oceanfront Villa',
      rating: 5,
      text:
        'Absolutely exceptional service for our beachfront villa. The marine-grade coatings have performed flawlessly for three years despite direct ocean exposure. The team was professional, respectful, and delivered results that exceeded our highest expectations. Worth every cent for this level of quality.',
    },
    {
      name: 'Michael Rousseau',
      location: 'Bantry Bay Penthouse',
      rating: 5,
      text:
        'Outstanding craftsmanship and attention to detail. Our penthouse looks absolutely stunning and the protective coatings give us complete peace of mind. The project management was impeccable with minimal disruption to our daily life. Highly recommend for luxury properties.',
    },
    {
      name: 'Samantha van Niekerk',
      location: 'Bantry Bay Heritage Home',
      rating: 5,
      text:
        'The restoration of our heritage property was handled with remarkable expertise and care. They understood the importance of preserving architectural integrity while providing modern protection. The results are breathtaking and have significantly enhanced our property value.',
    },
  ];

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-32 min-h-[600px]">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Luxury%20oceanfront%20villas%20in%20Bantry%20Bay%20Cape%20Town%20being%20painted%2C%20exclusive%20beachfront%20properties%20with%20Atlantic%20Ocean%20views%2C%20professional%20painters%20working%20on%20prestigious%20coastal%20homes%2C%20sophisticated%20architecture%20with%20marine-grade%20painting%20services&width=1920&height=600&seq=bantry-bay-hero-main&orientation=landscape"
            alt="Elite painting services in Bantry Bay, Cape Town"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Elite <span className="text-yellow-400">Bantry Bay Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Luxury painting services for Bantry Bay&apos;s most exclusive oceanfront properties.
              Specialized marine‑grade coatings and premium finishes for discerning clients in Cape Town&apos;s most prestigious coastal neighborhood.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </a>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-black mb-6">
              Expert Painting Services for Bantry Bay&apos;s Exclusive Coastal Properties
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                Bantry Bay represents the pinnacle of luxury coastal living in Cape Town, renowned for its spectacular oceanfront villas, exclusive residential complexes, and breathtaking Atlantic Ocean views. This prestigious neighborhood, nestled between Clifton and Sea Point, demands painting services that match its sophisticated character and can withstand the unique challenges of direct ocean exposure.
              </p>
              <p>
                Our specialized painting services for Bantry Bay properties combine premium marine-grade materials, master craftsmanship, and deep understanding of Atlantic coastal environmental factors. The neighborhood&apos;s direct exposure to salt-laden ocean air, intense UV radiation, and moisture-rich conditions requires advanced coating systems and meticulous application techniques that go far beyond standard painting practices.
              </p>
              <p>
                Whether you own an oceanfront villa with panoramic Atlantic views, a luxury penthouse in one of the area&apos;s prestigious complexes, or a heritage property requiring specialized restoration, our team delivers painting solutions that protect your significant investment while enhancing its aesthetic appeal. We understand that Bantry Bay properties represent some of Cape Town&apos;s most valuable real estate, and our services are designed to preserve and enhance their value through superior protection and stunning visual results.
              </p>
              <p>
                The unique microclimate of Bantry Bay, with its direct Atlantic Ocean exposure and minimal protection from prevailing winds, creates specific challenges for building exteriors and interiors. Salt spray from ocean waves can reach properties even on elevated positions, affecting surfaces and accelerating deterioration if not properly protected. Our comprehensive marine-grade coating systems provide exceptional protection against these environmental factors while maintaining the sophisticated appearance expected in this exclusive neighborhood.
              </p>
              <p>
                Bantry Bay&apos;s architectural landscape features a diverse mix of contemporary villas, heritage properties, and modern apartment complexes, each requiring specialized painting approaches. From preserving the character of historic homes to protecting cutting-edge contemporary designs, our team has the expertise and experience to handle any project with the professionalism and attention to detail that Bantry Bay residents expect and deserve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Comprehensive Luxury Painting Services in Bantry Bay
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions for Bantry Bay&apos;s exclusive oceanfront properties, luxury villas, and prestigious residential complexes. Our services are tailored to meet the exacting standards of this elite coastal neighborhood.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 hover:shadow-lg transition-shadow"
              >
                <div
                  className="w-16 h-16 flex items-center justify-center mx-auto mb-6"
                  style={{ backgroundColor: '#ffff00' }}
                >
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coastal Challenges Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-black mb-6">
              Understanding Bantry Bay&apos;s Unique Atlantic Coastal Painting Challenges
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                Bantry Bay&apos;s direct Atlantic Ocean exposure presents some of the most challenging environmental conditions for painted surfaces in Cape Town. The neighborhood&apos;s beachfront location means properties are constantly exposed to salt-laden ocean spray, which can cause premature paint failure, corrosion, and surface degradation if not properly addressed with specialized marine-grade protection systems.
              </p>
              <p>
                The prevailing south-easterly winds during summer months carry salt spray and moisture directly from ocean waves, affecting properties at all elevations throughout the neighborhood. This constant salt accumulation on painted surfaces accelerates deterioration and can lead to blistering, peeling, and discoloration if standard painting products and techniques are used. Our marine-grade systems are specifically engineered to resist these harsh conditions.
              </p>
              <p>
                UV radiation intensity in Bantry Bay is exceptionally high due to the reflective properties of the Atlantic Ocean and the lack of natural shade in many beachfront areas. This intense sun exposure causes rapid fading and chalking of inferior paint products, making the selection of premium UV-resistant coatings absolutely essential for maintaining long-term aesthetic appeal and protection in this demanding environment.
              </p>
              <p>
                Moisture management is particularly critical for Bantry Bay properties due to the combination of ocean humidity, morning fog, wind-driven rain, and occasional ocean spray reaching elevated properties. This moisture-rich environment can penetrate building envelopes if proper waterproofing and coating systems are not employed. Our comprehensive approach addresses these moisture challenges through advanced preparation techniques, specialized primers, and multi-layer protection systems designed specifically for Atlantic coastal conditions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Our Premium Painting Process for Bantry Bay Properties
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring exceptional results and long-lasting protection for your oceanfront property
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-black">{process.step}</span>
                </div>
                <h3 className="text-xl font-bold text-black mb-4">{process.title}</h3>
                <p className="text-gray-600 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Bantry Bay Properties Choose Our Elite Services
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Atlantic Coastal Expertise',
                    description:
                      'Specialized knowledge of oceanfront painting challenges and marine‑grade solutions for Bantry Bay properties. Our team has extensive experience working in this demanding environment and understands the specific requirements for long-lasting protection against direct Atlantic exposure.',
                  },
                  {
                    title: 'Luxury Excellence Standards',
                    description:
                      'Premium materials and meticulous attention to detail meeting the highest expectations of discerning clients. We use only top-tier products from leading manufacturers and employ master craftsmen who take pride in delivering flawless results that enhance property value.',
                  },
                  {
                    title: 'Minimal Lifestyle Disruption',
                    description:
                      'Flexible scheduling and efficient processes designed to minimize impact on luxury lifestyle and property operations. We coordinate carefully with residents and property managers to ensure maximum convenience and minimal inconvenience throughout the project.',
                  },
                  {
                    title: 'Superior Long-term Protection',
                    description:
                      'Advanced coating systems providing exceptional protection against salt air, UV damage, and Atlantic coastal weather. Our solutions are designed for maximum durability and longevity in the harshest marine environments, ensuring your investment is protected for years.',
                  },
                  {
                    title: 'Local Neighborhood Knowledge',
                    description:
                      'Deep understanding of Bantry Bay&apos;s architectural styles, building codes, and body corporate requirements. We navigate the unique aspects of this exclusive neighborhood with ease and professionalism, ensuring compliance and satisfaction.',
                  },
                  {
                    title: 'Comprehensive Warranty Coverage',
                    description:
                      'Extensive warranty coverage on both materials and workmanship, providing complete peace of mind for your investment. Our guarantees reflect our confidence in the quality and durability of our work in challenging coastal conditions.',
                  },
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20luxury%20Bantry%20Bay%20oceanfront%20property%2C%20exclusive%20beachfront%20villa%20painting%2C%20premium%20painting%20equipment%20and%20materials%2C%20Atlantic%20coastal%20property%20maintenance%20Cape%20Town&width=600&height=700&seq=bantry-bay-team-work&orientation=portrait"
                alt="Elite painting services in Bantry Bay"
                className="rounded-2xl shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent Bantry Bay Luxury Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our premium painting projects in Bantry Bay, demonstrating our commitment to
              luxury and excellence in this prestigious oceanfront neighborhood
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {recentProjects.map((project, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="w-full h-64">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">What Bantry Bay Clients Say</h2>
            <p className="text-xl text-gray-600">
              Testimonials from satisfied luxury property owners in Bantry Bay
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">
                  &quot;{testimonial.text}&quot;
                </p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Ready to Enhance Your Bantry Bay Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for an elite consultation and detailed quote. Experience the difference of
            luxury painting services designed specifically for Bantry Bay&apos;s exclusive oceanfront properties.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
            <a
              href="tel:+27123456789"
              className="bg-white text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}